import React, { useState, useEffect } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Checkbox,
    Button,
    TableContainer,
    Paper,
    Switch, FormControlLabel,
} from "@mui/material";
import DnDSelectedPatientsModal from "./DnDSelectedPatientsModal";
import DndHistoryModal from "./DndHistoryModal";
import { useAuth } from "../components/TokenHandler";
import { CircularProgress } from "@mui/material";


const DnDPatientView = ({ filteredPatients, fac_id, ouid,facility_name,facility_unique_id,provider_number }) => {
    console.log(fac_id, ouid);

    const { keycloak: kc } = useAuth();

    const [selectedPatients, setSelectedPatients] = useState([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [historyPatientRecords, setHistoryPatientRecords] = useState([]); // Store full history
    const [dnd_log, setDndLog] = useState({}); // Store all history records by patient_id
    const [viewDischarged, setViewDischarged] = useState(false);
    const [loading, setLoading] = useState(true);



    const fetchDndLog = async () => {
        setLoading(true);
        if (filteredPatients.length > 0 && kc?.token) {
            // Flatten all UUIDs into a single array
            const allUuids = filteredPatients.flatMap(p => p.patient_uuids || []);
            const allPatientUniqueIds = filteredPatients.flatMap(p => p.patient_unique_id || []);

            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/get_dnd_history_uuid`, {
                    method: "POST",
                    headers: {
                        "Authorization": `Bearer ${kc.token}`,
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify({ patient_uuid: allUuids,patient_unique_id:allPatientUniqueIds }) // ✅ correct input
                });

                if (response.ok) {
                    const data = await response.json(); // response is an array of records

                    // Build mapping: patient_id -> all records for its UUIDs
                    const processedData = {};
                    filteredPatients.forEach(patient => {
                        const patientId = patient.patient_id;
                        const uuids = patient.patient_uuids || [];

                        // Filter all records whose patient_uuid matches one of this patient's uuids
                        const records = data.filter(record =>
                            uuids.includes(record.patient_uuid)
                        );

                        processedData[patientId] = records;
                    });

                    console.log("processedData", processedData);
                    setDndLog(processedData);
                } else {
                    console.error("Failed to fetch DND log:", response.statusText);
                }
            } catch (error) {
                console.error("Error fetching DND log:", error);
            }
        }
        setLoading(false);
    };




    useEffect(() => {
        fetchDndLog();
    }, [filteredPatients]);

    //console.log(filteredPatients);

    const handleCheckboxChange = (patient) => {
        setSelectedPatients((prevSelected) =>
            prevSelected.some((p) => p.patient_id === patient.patient_id)
                ? prevSelected.filter((p) => p.patient_id !== patient.patient_id)
                : [...prevSelected, patient]
        );
    };

    const handleProcessSelected = () => {
        setModalOpen(true);
    };

    const handleRowClick = (patient) => {
        const patientHistory = dnd_log[patient.patient_id] || [];
        if (patientHistory.length > 0) {
            setHistoryPatientRecords(patientHistory);
            setHistoryModalOpen(true);
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box display="flex" justifyContent="flex-end" p={2}>
                <FormControlLabel
                    control={
                        <Switch
                            checked={viewDischarged}
                            onChange={() => setViewDischarged((prev) => !prev)}
                            color="primary"
                        />
                    }
                    label="View All Patients"
                />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleProcessSelected}
                    disabled={selectedPatients.length === 0}
                >
                    Process Selected
                </Button>
            </Box>
            <TableContainer component={Paper} style={{ maxHeight: 600 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Select</TableCell>
                            <TableCell>First Name</TableCell>
                            <TableCell>Last Name</TableCell>
                            <TableCell>Date of Birth</TableCell>
                            <TableCell>Gender</TableCell>
                            <TableCell>MRN ID</TableCell>
                            <TableCell>Internal Patient ID</TableCell>
                            <TableCell>Patient Status</TableCell>
                            <TableCell>CCM</TableCell>
                            <TableCell>RPMI</TableCell>
                            <TableCell>BHI</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredPatients
                            .filter((patient) => {
                                const status = (patient.status || "").trim().toLowerCase();
                                if (viewDischarged) return true;
                                return status !== "discharged" && status !== "";
                            })

                            .map((patient) => {
                                const patientRecords = dnd_log[patient.patient_id] || [];

                                // Normalize helpers
                                const norm = (v) => (v ?? "").toString().trim().toLowerCase();
                                const isTapestry = (s) => norm(s) === "tapestry opt out application";

                                const parseTs = (s) => {
                                    if (!s) return 0;
                                    if (/t/i.test(s)) return Date.parse(s) || 0;
                                    const withT = s.replace(" ", "T");
                                    return Date.parse(withT.endsWith("Z") ? withT : withT + "Z") || 0;
                                };

                                const asYesNo = (v) => {
                                    const s = norm(v);
                                    if (s === "yes" || s === "y" || s === "true") return "Yes";
                                    if (s === "no" || s === "n" || s === "false") return "No";
                                    return null;
                                };

                                // Get tapestry-only records
                                const tapestryRecords = patientRecords.filter((rec) => isTapestry(rec.source_system));

                                let ccmValue = "No Record";
                                let rpmValue = "No Record";
                                let bhiValue = "No Record";

                                if (tapestryRecords.length > 0) {
                                    // Pick most recent tapestry record
                                    const mostRecent = tapestryRecords.reduce((latest, current) => {
                                        const ld = parseTs(latest.modifiedon) || parseTs(latest.createdon);
                                        const cd = parseTs(current.modifiedon) || parseTs(current.createdon);
                                        return cd > ld ? current : latest;
                                    }, tapestryRecords[0]);

                                    // Use only ccm/rpm/bhi fields from tapestry record
                                    ccmValue = asYesNo(mostRecent.ccm) ?? "No Record";
                                    rpmValue = asYesNo(mostRecent.rpm) ?? "No Record";
                                    bhiValue = asYesNo(mostRecent.bhi) ?? "No Record";
                                } else {
                                    // Fallback: use calculated* fields
                                    const hasCCM = patientRecords.some((rec) => norm(rec.calculatedccm) === "yes");
                                    const hasRPM = patientRecords.some((rec) => norm(rec.calculatedrpm) === "yes");
                                    const hasBHI = patientRecords.some((rec) => norm(rec.calculatedbhi) === "yes");

                                    ccmValue = hasCCM ? "Yes" : "No Record";
                                    rpmValue = hasRPM ? "Yes" : "No Record";
                                    bhiValue = hasBHI ? "Yes" : "No Record";
                                }

                                return (
                                    <TableRow
                                        key={patient.patient_id}
                                        onClick={() => handleRowClick(patient)}
                                        style={{
                                            cursor: patientRecords.length > 0 ? "pointer" : "default",
                                            backgroundColor: patientRecords.length > 0 ? "#dea297" : "white",
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = patientRecords.length > 0
                                                ? "lightgrey"
                                                : "#f0f8ff";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = patientRecords.length > 0
                                                ? "#dea297"
                                                : "white";
                                        }}
                                    >
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                checked={selectedPatients.some((p) => p.patient_id === patient.patient_id)}
                                                onChange={() => handleCheckboxChange(patient)}
                                            />
                                        </TableCell>
                                        <TableCell>{patient.first_name}</TableCell>
                                        <TableCell>{patient.last_name}</TableCell>
                                        <TableCell>{patient.dob}</TableCell>
                                        <TableCell>{patient.gender}</TableCell>
                                        <TableCell>{patient.mrn_id}</TableCell>
                                        <TableCell>{patient.patient_id}</TableCell>
                                        <TableCell>{patient.status}</TableCell>

                                        {/* CCM */}
                                        <TableCell
                                            style={{ backgroundColor: ccmValue === "No" ? "#add8e6" : "inherit" }}
                                        >
                                            {ccmValue === "Yes"
                                                ? "Opt Out"
                                                : ccmValue === "No"
                                                    ? "Opt In"
                                                    : "No Record"}
                                        </TableCell>

                                        {/* RPM */}
                                        <TableCell
                                            style={{ backgroundColor: rpmValue === "No" ? "#add8e6" : "inherit" }}
                                        >
                                            {rpmValue === "Yes"
                                                ? "Opt Out"
                                                : rpmValue === "No"
                                                    ? "Opt In"
                                                    : "No Record"}
                                        </TableCell>

                                        {/* BHI */}
                                        <TableCell
                                            style={{ backgroundColor: bhiValue === "No" ? "#add8e6" : "inherit" }}
                                        >
                                            {bhiValue === "Yes"
                                                ? "Opt Out"
                                                : bhiValue === "No"
                                                    ? "Opt In"
                                                    : "No Record"}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        }
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Selected Patients Modal */}
            <DnDSelectedPatientsModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                selectedPatients={selectedPatients.map(patient => ({
                    ...patient,
                    ccm: dnd_log[patient.patient_id]?.[0]?.ccm || "No Record",
                    rpm: dnd_log[patient.patient_id]?.[0]?.rpm || "No Record",
                    rtm: dnd_log[patient.patient_id]?.[0]?.rtm || "No Record"
                }))}
                fac_id={fac_id}
                ouid={ouid}
                facility_name={facility_name}
            facility_unique_id={facility_unique_id}
                provider_number={provider_number}
                onSuccess={fetchDndLog}
            />


            {/* DND History Modal */}
            {historyPatientRecords.length > 0 && (
                <DndHistoryModal
                    isOpen={historyModalOpen}
                    onClose={() => setHistoryModalOpen(false)}
                    patientHistory={historyPatientRecords}
                />
            )}
        </Box>
    );
};

export default DnDPatientView;
